import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { AUTH } from "@/constants/testIds/app";
import { BookOpen } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/home");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Welcome back">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <Field label="Email">
          <input
            data-testid={AUTH.loginEmail}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input-cozy"
            placeholder="you@example.com"
          />
        </Field>
        <Field label="Password">
          <input
            data-testid={AUTH.loginPassword}
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-cozy"
            placeholder="••••••••"
          />
        </Field>
        {error && (
          <p data-testid={AUTH.loginError} className="text-sm text-[#8B3A3A] bg-[#F5E9E6] px-3 py-2 rounded-[10px]">
            {error}
          </p>
        )}
        <button
          data-testid={AUTH.loginSubmit}
          type="submit"
          disabled={busy}
          className="btn-primary mt-2"
        >
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
      <p className="text-sm text-[#2C2520]/70 mt-6 text-center">
        New here?{" "}
        <Link data-testid={AUTH.switchToSignup} to="/signup" className="text-[#4A5D4E] underline underline-offset-4">
          Create an account
        </Link>
      </p>
    </AuthShell>
  );
}

export function AuthShell({ title, children }) {
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-6 py-14">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2.5 justify-center mb-8">
          <img src="/logo.png" alt="Bibliotherapy Companion" className="w-11 h-11 object-contain" />
          <div className="font-serif-display text-[#2C2520] text-lg">Bibliotherapy Companion</div>
        </Link>
        <div className="bg-[#F5F2EA] border border-[#E8E3D5] rounded-[12px] p-8 shadow-[0_2px_8px_rgba(44,37,32,0.06)]">
          <h1 className="font-serif-display text-3xl text-[#2C2520] mb-6">{title}</h1>
          {children}
        </div>
        <style>{`
          .input-cozy {
            width: 100%;
            padding: 0.75rem 0.9rem;
            border-radius: 12px;
            background: #FDFBF7;
            border: 1px solid #E8E3D5;
            color: #2C2520;
            font-size: 0.95rem;
            transition: border-color .2s, background .2s;
          }
          .input-cozy:focus {
            outline: none;
            background: white;
            border-color: #4A5D4E;
            box-shadow: 0 0 0 3px rgba(74,93,78,0.12);
          }
          .btn-primary {
            width: 100%;
            padding: 0.85rem 1rem;
            border-radius: 12px;
            background: #4A5D4E;
            color: white;
            font-weight: 500;
            transition: background .2s, transform .1s;
          }
          .btn-primary:hover { background: #3D4D40; }
          .btn-primary:active { transform: scale(.98); }
          .btn-primary:disabled { opacity: .6; cursor: not-allowed; }
        `}</style>
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-xs tracking-[0.2em] uppercase text-[#4A5D4E] font-medium">{label}</span>
      {children}
    </label>
  );
}
